import { useCallback } from 'react';

import {
  DrawerNegativeButton,
  ModalBody,
  ModalFooter,
  ModalPrimaryButton,
  ModalWrapper,
  ModalWrapperProps,
} from '@zooplus/ui-components';

import { useTranslation } from '@hooks/useTranslations';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';

interface ModalProps {
  modalId: OverlayIds;
  disabledDangerBtn?: boolean;
  disabledSecondaryBtn?: boolean;
  disabledPrimaryBtn?: boolean;
  dataZta: string;
  dataZtaPrimaryBtn?: string;
  dataZtaDangerBtn?: string;
  handleSubmit?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  headingId: string;
  heading: string;
  size: ModalWrapperProps['size'];
  closeBtnAriaLabel: string;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  dangerButtonLabel?: string;
  isCloseOnBackdropClick?: boolean;
  isDismissible?: boolean;
  children: React.ReactNode;
}

/**
 * BaseModal provides a standardized modal layout with optional
 * primary and danger action buttons. It handles overlay visibility
 * through the OverlaysProvider and offers basic accessibility features.
 */

const BaseModal: React.FC<ModalProps> = ({
  modalId,
  heading,
  headingId,
  size,
  handleSubmit,
  onClose,
  dataZta,
  dataZtaPrimaryBtn = 'primaryModalBtnBottom',
  dataZtaDangerBtn = 'dangerModalBtnBottom',
  closeBtnAriaLabel,
  primaryButtonLabel,
  secondaryButtonLabel,
  dangerButtonLabel,
  isLoading = false,
  disabledDangerBtn = false,
  disabledPrimaryBtn = false,
  disabledSecondaryBtn = false,
  isCloseOnBackdropClick = false,
  isDismissible = true,
  children,
}) => {
  const { t } = useTranslation();
  const { checkIsActive, hideOverlay } = useOverlays();

  const closeModal = useCallback(() => {
    hideOverlay(modalId);
  }, [hideOverlay, modalId]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handleSubmit?.();
    },
    [handleSubmit],
  );

  const isActive = checkIsActive(modalId);
  const effectiveOnClose = onClose ?? closeModal;

  if (!isActive) {
    return null;
  }

  return (
    <ModalWrapper
      size={size}
      isOpen={isActive}
      onClose={effectiveOnClose}
      heading={heading}
      headingId={headingId}
      closeBtnAriaLabel={closeBtnAriaLabel}
      data-zta={dataZta}
      data-testId={dataZta}
      isLoading={!isDismissible}
      closeOnBackdropClick={isCloseOnBackdropClick}>
      <ModalBody data-zta={`${dataZta}-body`} data-testid={`${dataZta}-body`}>
        {children}
      </ModalBody>
      <ModalFooter>
        <DrawerNegativeButton
          onClick={effectiveOnClose}
          disabled={disabledSecondaryBtn || !isDismissible}
          data-zta="closeModalBtnBottom"
          data-testid="closeModalBtnBottom">
          {secondaryButtonLabel ?? t('general.button.back')}
        </DrawerNegativeButton>

        {!!primaryButtonLabel && (
          <ModalPrimaryButton
            onClick={handleClick}
            disabled={disabledPrimaryBtn}
            isLoading={isLoading}
            loadingIconType="paw"
            data-zta={dataZtaPrimaryBtn}
            data-testId={dataZtaPrimaryBtn}>
            {primaryButtonLabel}
          </ModalPrimaryButton>
        )}
        {!!dangerButtonLabel && (
          <ModalPrimaryButton
            onClick={handleClick}
            disabled={disabledDangerBtn}
            isLoading={isLoading}
            loadingIconType="paw"
            data-zta={dataZtaDangerBtn}
            data-testId={dataZtaDangerBtn}>
            {dangerButtonLabel}
          </ModalPrimaryButton>
        )}
      </ModalFooter>
    </ModalWrapper>
  );
};

export default BaseModal;
